/**
 * 患者の SAS のセッション（Issue #473）。ops/backoffice の authentication/session.ts を手本にする。
 *
 * localStorage が使えない（プライベートブラウズ・保存の拒否）ときは、セッションが無いものとして扱う。
 */

export type PatientSession = {
  accessToken: string;
  refreshToken: string;
};

const storageKey = "sas.patient.session";

function parse(json: string | null): PatientSession | null {
  if (json === null) return null;
  try {
    const value: unknown = JSON.parse(json);
    if (typeof value !== "object" || value === null) return null;
    const { accessToken, refreshToken } = value as Record<string, unknown>;
    if (
      typeof accessToken !== "string" ||
      accessToken === "" ||
      typeof refreshToken !== "string" ||
      refreshToken === ""
    ) {
      return null;
    }
    return { accessToken, refreshToken };
  } catch {
    return null;
  }
}

/** 無い・壊れている・読めないなら null。 */
export function currentPatientSession(): PatientSession | null {
  try {
    return parse(window.localStorage.getItem(storageKey));
  } catch {
    return null;
  }
}

/**
 * アクセストークンの sub（患者の利用者 ID）。読めなければ null（中身は確かめない。見分けるためだけに使う）。
 */
function subjectOf(session: PatientSession | null): string | null {
  if (session === null) return null;
  const payload = session.accessToken.split(".")[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const value: unknown = JSON.parse(
      window.atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")),
    );
    if (typeof value !== "object" || value === null) return null;
    const { sub } = value as Record<string, unknown>;
    return typeof sub === "string" ? sub : null;
  } catch {
    return null;
  }
}

type PatientSessionChangeListener = (change: { inOtherTab: boolean }) => void;
const listeners = new Set<PatientSessionChangeListener>();

/**
 * ログインの有無か、ログインしている患者（アクセストークンの sub）が変わったとき（ログイン・ログアウト・期限切れでの削除・
 * ほかのタブでの別の患者のログイン）に呼ぶ。同じ患者のトークンの取り替えとパスワード変更では呼ばない。
 *
 * @return 登録をやめる関数
 */
export function subscribePatientSessionChange(
  listener: PatientSessionChangeListener,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyIfSignedInChanged(
  previous: PatientSession | null,
  next: PatientSession | null,
  inOtherTab: boolean,
): void {
  if (
    (previous === null) !== (next === null) ||
    subjectOf(previous) !== subjectOf(next)
  ) {
    listeners.forEach((listener) => listener({ inOtherTab }));
  }
}

/** @return 書けたか（localStorage が使えない端末では false） */
function store(next: PatientSession | null): boolean {
  const previous = currentPatientSession();
  let written = true;
  try {
    if (next === null) window.localStorage.removeItem(storageKey);
    else
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          accessToken: next.accessToken,
          refreshToken: next.refreshToken,
        }),
      );
  } catch {
    // 保存できない端末では、このページの間もセッションを持てない（呼び手が書けなかったことを扱う）
    written = false;
  }
  notifyIfSignedInChanged(previous, next, false);
  return written;
}

/** @return 書けたか。ログインでは、書けなければログインを終えられない（patientAuthenticationApi.ts） */
export function savePatientSession(session: PatientSession): boolean {
  return store(session);
}

export function clearPatientSession(): void {
  store(null);
}

// ほかのタブでのログイン・ログアウトも、このタブの変更として知らせる。
// key が null なのは、保存しているものがすべて消されたとき（ログアウトとして扱う）
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === storageKey) {
      notifyIfSignedInChanged(
        parse(event.oldValue),
        parse(event.newValue),
        true,
      );
    } else if (event.key === null) {
      listeners.forEach((listener) => listener({ inOtherTab: true }));
    }
  });
}
